import { useState, useCallback, useRef } from 'react';
import { callLLMWithBackoff } from '../utils/ai';
import { getReviewQueue, saveQuizSession } from '../utils/quizStorage';
import { t } from '../i18n';
import { getFlashcardSystemPrompt, constructFlashcardUserPrompt, parseFlashcardResponse } from '../prompts/flashcard';
import { getQuizOpenSystemPrompt, constructQuizOpenPrompt, parseQuizOpenResponse } from '../prompts/quiz';

const getNeighbors = (node, allNodes, allConns) => {
  const neighbors = [];
  allConns.forEach(conn => {
    if (conn.from === node.id) {
      const n = allNodes.find(n => n.id === conn.to);
      if (n) neighbors.push(n);
    }
    if (conn.to === node.id) {
      const n = allNodes.find(n => n.id === conn.from);
      if (n) neighbors.push(n);
    }
  });
  return neighbors;
};

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export function useQuizSession({ nodes, connections, mapId, provider, lang = 'en' }) {
  const [phase, setPhase] = useState('idle');
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [error, setError] = useState(null);
  const [lastSession, setLastSession] = useState(null);
  const abortRef = useRef(false);

  const selectNodes = useCallback((count) => {
    const reviewQueue = getReviewQueue(mapId);
    const reviewNodeIds = new Set(reviewQueue.map(q => q.nodeId));
    const validNodes = nodes.filter(n => !n.banned && n.title);
    const reviewNodes = validNodes.filter(n => reviewNodeIds.has(n.id));
    const freshNodes = shuffle(validNodes.filter(n => !reviewNodeIds.has(n.id)));
    return [...reviewNodes, ...freshNodes].slice(0, count);
  }, [nodes, mapId]);

  const generateQuestion = useCallback(async (node, type, rootNode) => {
    const neighbors = getNeighbors(node, nodes, connections);
    if (type === 'closed') {
      const prompt = constructFlashcardUserPrompt(node, neighbors, node.summary || '', rootNode);
      const response = await callLLMWithBackoff(provider, getFlashcardSystemPrompt(lang), prompt);
      const parsed = parseFlashcardResponse(response, node);
      return { nodeId: node.id, nodeTitle: node.title, type: 'closed', front: parsed.front, back: parsed.back };
    } else {
      const prompt = constructQuizOpenPrompt(node, neighbors, rootNode);
      const response = await callLLMWithBackoff(provider, getQuizOpenSystemPrompt(lang), prompt);
      const parsed = parseQuizOpenResponse(response, node);
      return { nodeId: node.id, nodeTitle: node.title, type: 'open', front: parsed.question, back: parsed.answer };
    }
  }, [nodes, connections, provider, lang]);

  const startQuiz = useCallback(async (config) => {
    if (!provider) { setError(t('quiz.noProvider', lang)); return; }
    const { count, type } = config;
    setPhase('generating');
    setGenerationProgress(0);
    setError(null);
    abortRef.current = false;

    const selected = selectNodes(count);
    if (selected.length === 0) {
      setError(t('quiz.noNodes', lang));
      setPhase('idle');
      return;
    }

    const rootNode = nodes.find(n => n.type === 'problem') || nodes[0];
    const generated = [];

    for (let i = 0; i < selected.length; i++) {
      if (abortRef.current) return;
      const node = selected[i];
      const qType = type === 'mixed' ? (i % 2 === 0 ? 'closed' : 'open') : type;
      try {
        generated.push(await generateQuestion(node, qType, rootNode));
      } catch {
        generated.push({
          nodeId: node.id,
          nodeTitle: node.title,
          type: qType,
          front: qType === 'closed'
            ? (node.summary || `Definisci: ${node.title}`)
            : `Spiega il concetto: ${node.title}`,
          back: node.description || node.summary || node.title,
        });
      }
      setGenerationProgress(Math.round(((i + 1) / selected.length) * 100));
    }

    setQuestions(generated);
    setAnswers([]);
    setCurrentIndex(0);
    setPhase('session');
  }, [provider, selectNodes, generateQuestion, nodes, lang]);

  const answerQuestion = useCallback((isCorrect, userAnswer = '') => {
    setAnswers(prev => [...prev, { isCorrect, userAnswer }]);
  }, []);

  const nextQuestion = useCallback(() => {
    setCurrentIndex(prev => prev + 1);
  }, []);

  const finishQuiz = useCallback((config) => {
    const sessionQuestions = questions.map((q, i) => ({
      ...q,
      isCorrect: answers[i]?.isCorrect ?? false,
      userAnswer: answers[i]?.userAnswer ?? '',
    }));
    const correct = sessionQuestions.filter(q => q.isCorrect).length;
    const session = {
      id: `qs_${Date.now()}`,
      date: new Date().toISOString(),
      config,
      questions: sessionQuestions,
      score: { correct, total: sessionQuestions.length },
    };
    saveQuizSession(mapId, session);
    setLastSession(session);
    setPhase('results');
    return session;
  }, [questions, answers, mapId]);

  const reset = useCallback(() => {
    abortRef.current = true;
    setPhase('idle');
    setQuestions([]);
    setAnswers([]);
    setCurrentIndex(0);
    setGenerationProgress(0);
    setError(null);
  }, []);

  return {
    phase,
    questions,
    currentIndex,
    answers,
    generationProgress,
    error,
    lastSession,
    currentQuestion: questions[currentIndex] || null,
    isLastQuestion: currentIndex >= questions.length - 1,
    reviewCount: getReviewQueue(mapId).length,
    startQuiz,
    answerQuestion,
    nextQuestion,
    finishQuiz,
    reset,
  };
}
